import * as React from 'react';
import { NavigateFunction, Location } from 'react-router-dom';
import { getSearchParameter } from '../helpers/location-helper';
import { getCoreAPIUrl } from '../helpers/urlHelper';
import { redirectToDecline, redirectToSuccess } from '../helpers/parent-helper';
import { paymentResponseHandler } from '../helpers/payment-helper';
import { setTemplateItem, setParameters, IAction as ITemplateAction } from './TemplateContext';
import { commonReducerActions, IAction as CommonAction } from './CommonContext';
import { getChallenge, IChallengeAnswers, IEligibilityApproveResponse } from '../helpers/fetch-service';
import { IConfiguration } from './store-data';
import { logger, loggerError } from '../helpers/logger';
import * as Sentry from '@sentry/react';

enum paymentReducerActions {
  SET_PAYMENT_LOADING = 'SET_PAYMENT_LOADING',
  SET_PAYMENT_START = 'SET_PAYMENT_START',
  SET_PAYMENT_END = 'SET_PAYMENT_END',
}

export interface IAction {
  type: string;
  payload?: any;
}
interface IState {
  paymentLoading: boolean;
}
type PaymentProviderProps = { children: React.ReactNode };

const PaymentStateContext = React.createContext<{ state: IState; dispatch: React.Dispatch<IAction> } | undefined>(
  undefined,
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function paymentReducer(state: IState, action: IAction) {
  switch (action.type) {
    case paymentReducerActions.SET_PAYMENT_LOADING: {
      return { ...state, paymentItem: action.payload };
    }
    case paymentReducerActions.SET_PAYMENT_START: {
      return { ...state, paymentLoading: true };
    }
    case paymentReducerActions.SET_PAYMENT_END: {
      return { ...state, paymentLoading: false };
    }
    default: {
      return state;
      // throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function PaymentProvider({ children }: PaymentProviderProps) {
  const [state, dispatch] = React.useReducer(paymentReducer, {
    paymentLoading: false,
  });
  const value = { state, dispatch };

  return <PaymentStateContext.Provider value={value}>{children}</PaymentStateContext.Provider>;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function usePayment() {
  const context = React.useContext(PaymentStateContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
}

const declinedCallback = (navigate?: NavigateFunction): void => {
  // if (navigate) {
  //   // navigate('/decline');
  // } else {
  //   redirectToDecline();
  // }
  redirectToDecline();
};

const approveCallback = (navigate?: NavigateFunction): void => {
  // eslint-disable-next-line no-restricted-globals
  parent.postMessage(
    { message: 'demoMessage', value: '<span class="green">FlexFactor: transaction approved.</span>' },
    '*',
  );
  // if (navigate) {
  //   navigate('/success');
  // } else {
  //   redirectToSuccess();
  // }
  redirectToSuccess();
};

const challengeCallback = (
  dispatch: React.Dispatch<ITemplateAction>,
  commonDispatch: React.Dispatch<CommonAction>,
): void => {
  setTemplateItem(dispatch, null);
  setParameters(commonDispatch, {});
  console.log('getChallenge----2');
  getChallenge(commonDispatch);
};

export const eligibilityApprove = (
  dispatch: React.Dispatch<IAction>,
  navigate: NavigateFunction,
  commonDispatch: React.Dispatch<CommonAction>,
): void => {
  dispatch({ type: paymentReducerActions.SET_PAYMENT_START });

  const orderSessionKey = getSearchParameter('orderSessionKey');
  const mid = getSearchParameter('mid');
  const senseKey = getSearchParameter('senseKey');

  const url = `${getCoreAPIUrl('eligibility')}/approve`;
  const body = {
    orderSessionKey,
    senseKey,
    mid,
  };
  const options = {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return fetch(url, options)
    .then(response => response.json())
    .then(response => {
      paymentResponseHandler({
        response,
        approveCallback: () => approveCallback(),
        declinedCallback: () => declinedCallback(navigate),
        challengeCallback: () => challengeCallback(dispatch, commonDispatch),
      });
      dispatch({ type: paymentReducerActions.SET_PAYMENT_END });
    })
    .catch(error => {
      dispatch({ type: paymentReducerActions.SET_PAYMENT_LOADING, payload: false });
      loggerError(error);
      redirectToDecline();
    });
};

interface ISendChallengeAnswersProps {
  answers: IChallengeAnswers;
  dispatch: React.Dispatch<IAction>;
  navigate: NavigateFunction;
  location: Location;
  configuration: IConfiguration;
  commonDispatch: React.Dispatch<CommonAction>;
  templateDispatch: React.Dispatch<ITemplateAction>;
  customCallback?: () => void;
}
export function sendAnswers(props: ISendChallengeAnswersProps): Promise<IEligibilityApproveResponse> {
  const { answers, dispatch, commonDispatch, templateDispatch, customCallback } = props;

  dispatch({ type: paymentReducerActions.SET_PAYMENT_START });

  // eslint-disable-next-line no-restricted-globals
  parent.postMessage(
    { message: 'demoMessage', value: '<span class="green">FlexFactor: sending challenge answers.</span>' },
    '*',
  );

  const orderSessionKey = getSearchParameter('orderSessionKey');
  const mid = getSearchParameter('mid');
  const senseKey = getSearchParameter('senseKey');

  const url = `${getCoreAPIUrl('eligibility')}/next`;
  // FIXME language
  const body = {
    orderSessionKey,
    senseKey,
    mid,
    language: 'string',
    answers,
  };

  const options = {
    method: 'PUT',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };

  commonDispatch({ type: commonReducerActions.SET_CHALLENGE_DATA, payload: null });

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return fetch(url, options)
    .then(response => response.json())
    .then(response => {
      paymentResponseHandler({
        response,
        approveCallback: () => {
          // eslint-disable-next-line no-restricted-globals
          parent.postMessage(
            { message: 'demoMessage', value: '<span class="green">FlexCharge: transaction approved.</span>' },
            '*',
          );
          redirectToSuccess();
          // switch (configuration?.consentFlowConfiguration) {
          //   case 3:
          //   case 1:
          //     redirectToSuccess();
          //     break;
          //   case 2:
          //   default:
          //     navigate('/success');
          // }
        },
        declinedCallback: () => {
          // eslint-disable-next-line no-restricted-globals
          parent.postMessage(
            { message: 'demoMessage', value: '<span class="green">FlexCharge: transaction declined.</span>' },
            '*',
          );
          redirectToDecline();
          // switch (configuration?.consentFlowConfiguration) {
          //   case 3:
          //   case 1:
          //     redirectToSuccess();
          //     break;
          //   case 2:
          //   default:
          //     navigate('/decline');
          // }
        },
        challengeCallback: () =>
          customCallback ? customCallback() : challengeCallback(templateDispatch, commonDispatch),
      });
      dispatch({ type: paymentReducerActions.SET_PAYMENT_END });
    })
    .catch(error => {
      Sentry.captureMessage('WidgetShell: POST send answers error', error.message);
      logger({ name: 'WidgetShell: POST send answers error', message: error.message });

      loggerError(error);
      dispatch({ type: paymentReducerActions.SET_PAYMENT_LOADING, payload: false });
      redirectToDecline();
    });
}

export { PaymentProvider, usePayment };
